<script lang="ts" setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import type { Project, ProjectWorkflowView } from "@/types";
import DataView from "@/components/DataView.vue";
import type {Config} from "datatables.net";
import RunWorkflowModal from "./RunWorkflowModal.vue";
import CustomizeWorkflowModal from "@/views/projects/components/CustomizeWorkflowModal.vue";
import RevertCustomizationModal from "@/views/projects/components/RevertCustomizationModal.vue";
import DeleteWorkflowModal from "@/views/projects/components/DeleteWorkflowModal.vue";
import { useBuilderNavigation } from "@/views/builder/composables/useBuilderNavigation";
import { useRemoveWorkflowCustomization, useDeleteWorkflow } from "@/queries/projects";

const router = useRouter();

interface Props {
  project: Project | null;
  workflows: ProjectWorkflowView[];
  isPublisher?: boolean; // Add this prop to determine if project is publisher
  isProductlessProject?: boolean; // Add this prop to determine if project is productless
  isPublisherMasterProject?: boolean; // Add this prop to determine if project is publisher master
  productName?: string; // The name of the product this project subscribes to
  productVersion?: string; // Version number from the product subscription (e.g. "2.1"); undefined when not yet synced
  // Schedule ids per workflow id for this project — drives the "scheduled" indicator. Empty when none.
  scheduleIdsByWorkflowId?: Record<string, string[]>;
}

const props = defineProps<Props>();

function scheduleIdsFor(workflowId: string): string[] {
  return props.scheduleIdsByWorkflowId?.[workflowId] ?? [];
}

// Indicator click → project's Schedules tab. Deep-open the schedule inline when there's exactly one;
// otherwise just land on the (short) list.
function viewSchedules(workflowId: string) {
  if (!props.project?.id) return;
  const ids = scheduleIdsFor(workflowId);
  const query = ids.length === 1 ? { open: ids[0] } : undefined;
  router.push({ path: `/projects/${props.project.id}/schedules`, query });
}

// Run opens the input modal (always — it collects any {{ input.* }} values, or just confirms the run
// when the workflow declares none). The modal owns the readiness gate + "Run anyway" confirm and the
// post-run navigation.
const showRunModal = ref(false);
const runTarget = ref<ProjectWorkflowView | null>(null);

function runWorkflow(workflowId: string) {
  runTarget.value = props.workflows.find((w) => w.id === workflowId) ?? null;
  showRunModal.value = true;
}

function addWorkflow() {
  if (props.project?.id) {
    router.push(`/projects/${props.project.id}/builder/new`);
  }
}

const canAddWorkflow = computed(() => props.isProductlessProject || props.isPublisherMasterProject);

const customizedCount = computed(() => props.workflows.filter((w) => w.isCustomized).length);

// Subscriber-only customize flow.
// Subscribers don't own the master workflow — clicking "Customize" forks it into an editable copy
// owned by their project. Idempotent on the backend; click again on an already-customized workflow
// just navigates to the existing fork.
const isSubscriberProject = computed(
  () => !props.isPublisher && !props.isPublisherMasterProject && !props.isProductlessProject
);

const projectIdRef = computed(() => props.project?.id ?? "");
// All builder entry points route through the shared navigation resolver — it owns the fork-vs-master
// decision and the confirm-before-forking flow, so this tab and the builder stay in lockstep.
const {
  openInBuilder,
  requestFork,
  confirmFork,
  cancelFork,
  showCustomizeModal,
  customizeTarget,
  customizeWorkflowPending,
  customizeWorkflowError,
} = useBuilderNavigation(projectIdRef);

// Revert customization: the any-time inverse of Customize. Confirm first (discards the fork), then
// remove the pin so the resolver serves the publisher's published version again.
const {
  removeCustomizationAsync,
  removeCustomizationPending,
  removeCustomizationError,
  removeCustomizationReset,
} = useRemoveWorkflowCustomization(projectIdRef);
const showRevertModal = ref(false);
const revertTarget = ref<ProjectWorkflowView | null>(null);

function requestRevert(row: ProjectWorkflowView) {
  removeCustomizationReset();
  revertTarget.value = row;
  showRevertModal.value = true;
}

async function confirmRevert() {
  if (!revertTarget.value) return;
  try {
    await removeCustomizationAsync(revertTarget.value.id);
    showRevertModal.value = false;
    revertTarget.value = null;
  } catch {
    // Error surfaces in the modal via removeCustomizationError; keep it open so the user can retry.
  }
}

function cancelRevert() {
  if (removeCustomizationPending.value) return;
  showRevertModal.value = false;
  revertTarget.value = null;
}

// Delete a workflow (non-subscriber projects). Confirm first — it's permanent. A 409 (blocked by an
// in-flight run, active schedule, shipped product version, or sub-workflow caller) surfaces in the
// modal via deleteWorkflowError so the user can read it and cancel/retry.
const {
  deleteWorkflowAsync,
  deleteWorkflowPending,
  deleteWorkflowError,
  deleteWorkflowReset,
} = useDeleteWorkflow(projectIdRef);
const showDeleteModal = ref(false);
const deleteTarget = ref<ProjectWorkflowView | null>(null);

function requestDelete(row: ProjectWorkflowView) {
  deleteWorkflowReset();
  deleteTarget.value = row;
  showDeleteModal.value = true;
}

async function confirmDelete() {
  if (!deleteTarget.value) return;
  try {
    await deleteWorkflowAsync(deleteTarget.value.id);
    showDeleteModal.value = false;
    deleteTarget.value = null;
  } catch {
    // Error surfaces in the modal via deleteWorkflowError; keep it open so the user can read/retry.
  }
}

function cancelDelete() {
  if (deleteWorkflowPending.value) return;
  showDeleteModal.value = false;
  deleteTarget.value = null;
}

// Edit/Customize click:
//   • Subscriber + not customized → confirm-before-forking (shared modal), then fork + open.
//   • Everything else (subscriber fork, master, DIY) → resolve idempotently and open the editor.
function editOrCustomize(row: ProjectWorkflowView) {
  const nav = { id: row.id, name: row.name, isCustomized: row.isCustomized };
  if (isSubscriberProject.value && !row.isCustomized) {
    requestFork(nav, "editor");
    return;
  }
  openInBuilder(props.project, nav, "editor");
}

// Version badge markup shared by the table column and the mobile card.
function versionBadges(row: any): string {
  const draftSuffix = row.hasDraftChanges
    ? ` <span class="badge bg-warning text-dark ms-1" title="Unpublished edits in progress">Draft</span>`
    : "";

  // Operational state, orthogonal to the lifecycle badge — a workflow can be Published AND
  // switched off. Only shown when off; active is the default and needs no badge.
  const inactiveSuffix = row.isActive === false
    ? ` <span class="badge bg-secondary ms-1" title="Deactivated — won't run"><i class="bi bi-pause-circle me-1"></i>Inactive</span>`
    : "";

  if (row.isCustomized) {
    return `<span class="badge bg-info" title="You've customized this workflow — your fork won't receive automatic updates">Customized</span>${draftSuffix}${inactiveSuffix}`;
  }
  if (row.version === 'Draft') {
    return `<span class="badge bg-warning text-dark" title="Never published">Draft</span>${inactiveSuffix}`;
  }
  return `<span class="badge bg-success" title="Published version">${row.version}</span>${draftSuffix}${inactiveSuffix}`;
}

function formatLastRun(data: string | null): string {
  return data ? new Date(data).toLocaleString() : "Never";
}

// One unified "Version" column that handles all three project types:
//   • Subscriber, customized → blue "Customized" (+ "Draft" suffix if unapplied edits)
//   • Subscriber, base / Publisher master / DIY published → green "vN" (+ "Draft" suffix if mid-edit)
//   • Master / DIY never published → warning "Draft"
// The Draft suffix is driven by HasDraftChanges from the backend, populated for any workflow that
// has a published version with unpublished edits beyond it.
const options = computed<Config>(() => {
  const lastRunColumn = {
    data: 'lastRun',
    title: 'Last Run',
    render: function (data: string | null) {
      if (!data) return '<span class="text-muted">Never</span>';
      const d = new Date(data);
      return d.toLocaleString();
    },
  };
  const successRateColumn = {
    data: 'successRate',
    title: 'Success Rate',
    render: function (data: any, _type: any, row: any) {
      if (!row.lastRun) return '<span class="text-muted">—</span>';
      const rate = Number(data) || 0;
      const cls = rate >= 90 ? 'text-success' : rate >= 70 ? 'text-warning' : 'text-danger';
      return `<span class="${cls}">${rate.toFixed(0)}%</span>`;
    },
  };
  const actionsColumn = {
    data: 'actions',
    title: 'Actions',
    orderable: false,
    searchable: false,
    render: '#actions',
  };

  const versionColumn = {
    data: 'version',
    title: 'Version',
    render: (_data: any, _type: any, row: any) => versionBadges(row),
  };

  return {
    layout: { topEnd: '', bottomStart: '', bottomEnd: '' },
    columns: [
      { data: 'name', title: 'Workflow Name', render: '#name' },
      versionColumn,
      lastRunColumn,
      successRateColumn,
      actionsColumn,
    ],
  } as Config;
});
</script>

<template>
  <div class="tab-pane fade show active">
    <!-- Compact Version Context Banner for Non-Publisher, Non-Productless Projects -->
    <div
      v-if="
        !props.isPublisher &&
        !props.isProductlessProject &&
        !props.isPublisherMasterProject
      "
      class="compact-banner mb-3"
    >
      <div class="banner-info">
        Using {{ props.productName || "Product" }}<template v-if="props.productVersion"> v{{ props.productVersion }}</template>
        <template v-if="customizedCount > 0">
          • {{ customizedCount }} {{ customizedCount === 1 ? "workflow" : "workflows" }} customized
        </template>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="props.workflows.length === 0" class="text-center py-5">
      <i class="bi bi-diagram-3 display-1 text-muted"></i>
      <h3 class="mt-3 text-muted">No workflows yet</h3>
      <p class="text-muted">
        Once workflows are added to this project, they will appear here.
      </p>
      <button v-if="canAddWorkflow" class="bb-btn-primary-cta mt-2" @click="addWorkflow">
        <i class="bi bi-plus-circle me-1"></i> New Workflow
      </button>
    </div>

    <!-- Workflows Table -->
    <template v-else>
      <div v-if="canAddWorkflow" class="d-flex justify-content-end mb-3">
        <button class="bb-btn-primary-cta" @click="addWorkflow">
          <i class="bi bi-plus-circle me-1"></i> New Workflow
        </button>
      </div>

      <DataView
          :data=props.workflows
          :view-mode="'table'"
          :options=options
      >
        <!-- Workflow name + inline "scheduled" indicator -->
        <template #name="slotProps">
          <span class="d-inline-flex align-items-center gap-2">
            <span>{{ slotProps.rowData.name }}</span>
            <button
              v-if="scheduleIdsFor(slotProps.rowData.id).length > 0"
              class="btn btn-sm btn-link text-decoration-none p-0 d-inline-flex align-items-center gap-1"
              :title="`View schedule${scheduleIdsFor(slotProps.rowData.id).length === 1 ? '' : 's'} for this workflow`"
              @click="viewSchedules(slotProps.rowData.id)"
            >
              <i class="bi bi-calendar-check"></i>
              <span>{{ scheduleIdsFor(slotProps.rowData.id).length }}</span>
            </button>
          </span>
        </template>

        <!-- Actions column slot -->
        <template #actions="slotProps">
          <div class="d-flex gap-2">
            <button
                v-if="isSubscriberProject && !slotProps.rowData.isCustomized"
                class="bb-btn-secondary-cta btn-sm"
                title="Customize this workflow — creates your own editable copy"
                :disabled="customizeWorkflowPending"
                @click="editOrCustomize(slotProps.rowData)"
            ><i class="bi bi-wrench me-1"></i>Customize</button>
            <button
                v-else
                class="bb-btn-secondary-cta btn-sm"
                :title="isSubscriberProject ? 'Edit your customized fork' : 'Edit Workflow'"
                :disabled="customizeWorkflowPending"
                @click="editOrCustomize(slotProps.rowData)"
            ><i class="bi bi-wrench me-1"></i>Edit</button>
            <button
              class="bb-btn-secondary-cta btn-sm"
              title="Run Workflow"
              @click="runWorkflow(slotProps.rowData.id)"
            ><i class="bi bi-play me-1"></i>Run</button>
            <button
                v-if="isSubscriberProject && slotProps.rowData.isCustomized"
                class="bb-btn-danger btn-sm"
                title="Discard your customization and revert to the published version"
                :disabled="removeCustomizationPending"
                @click="requestRevert(slotProps.rowData)"
            ><i class="bi bi-arrow-counterclockwise me-1"></i>Revert</button>
            <button
                v-if="!isSubscriberProject"
                class="bb-btn-danger btn-sm"
                title="Delete Workflow"
                :disabled="deleteWorkflowPending"
                @click="requestDelete(slotProps.rowData)"
            ><i class="bi bi-trash me-1"></i>Delete</button>
          </div>
        </template>

        <!-- Mobile / grid card — clean layout without per-field labels -->
        <template #grid-card="{ row }">
          <div class="card h-100 shadow-sm">
            <div class="card-body d-flex flex-column gap-2">
              <!-- Name + version/state badges -->
              <div class="d-flex align-items-start justify-content-between gap-2">
                <h5 class="mb-0 text-truncate">{{ row.name }}</h5>
                <span class="flex-shrink-0" v-html="versionBadges(row)"></span>
              </div>

              <!-- Meta: last run + success rate -->
              <div class="d-flex flex-wrap align-items-center gap-3 small text-muted">
                <span><i class="bi bi-clock-history me-1"></i>{{ formatLastRun(row.lastRun) }}</span>
                <span v-if="row.lastRun">
                  <i class="bi bi-graph-up me-1"></i>
                  <span
                    :class="(Number(row.successRate) || 0) >= 90 ? 'text-success'
                      : (Number(row.successRate) || 0) >= 70 ? 'text-warning' : 'text-danger'"
                  >{{ (Number(row.successRate) || 0).toFixed(0) }}%</span>
                </span>
              </div>

              <!-- Actions -->
              <div class="d-flex flex-wrap gap-2 mt-auto pt-2">
                <button
                    v-if="isSubscriberProject && !row.isCustomized"
                    class="bb-btn-secondary-cta btn-sm"
                    :disabled="customizeWorkflowPending"
                    @click="editOrCustomize(row)"
                ><i class="bi bi-wrench me-1"></i>Customize</button>
                <button
                    v-else
                    class="bb-btn-secondary-cta btn-sm"
                    :disabled="customizeWorkflowPending"
                    @click="editOrCustomize(row)"
                ><i class="bi bi-wrench me-1"></i>Edit</button>
                <button
                    class="bb-btn-secondary-cta btn-sm"
                    @click="runWorkflow(row.id)"
                ><i class="bi bi-play me-1"></i>Run</button>
                <button
                    v-if="isSubscriberProject && row.isCustomized"
                    class="bb-btn-danger btn-sm"
                    title="Discard your customization and revert to the published version"
                    :disabled="removeCustomizationPending"
                    @click="requestRevert(row)"
                ><i class="bi bi-arrow-counterclockwise me-1"></i>Revert</button>
                <button
                    v-if="!isSubscriberProject"
                    class="bb-btn-danger btn-sm ms-auto"
                    title="Delete Workflow"
                    :disabled="deleteWorkflowPending"
                    @click="requestDelete(row)"
                ><i class="bi bi-trash me-1"></i>Delete</button>
              </div>
            </div>
          </div>
        </template>
      </DataView>
    </template>

    <!-- Customize confirmation modal (shared with the Builder's edit gate). -->
    <CustomizeWorkflowModal
      :show="showCustomizeModal"
      :workflow-name="customizeTarget?.name"
      :pending="customizeWorkflowPending"
      :error-message="customizeWorkflowError?.message"
      @confirm="confirmFork"
      @cancel="cancelFork"
    />

    <!-- Revert confirmation modal — the any-time inverse of Customize. -->
    <RevertCustomizationModal
      :show="showRevertModal"
      :workflow-name="revertTarget?.name"
      :pending="removeCustomizationPending"
      :error-message="removeCustomizationError?.message"
      @confirm="confirmRevert"
      @cancel="cancelRevert"
    />

    <!-- Delete confirmation modal — permanent; a 409 blocker surfaces inline. -->
    <DeleteWorkflowModal
      :show="showDeleteModal"
      :workflow-name="deleteTarget?.name"
      :pending="deleteWorkflowPending"
      :error-message="deleteWorkflowError?.userMessage"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />

    <!-- Run modal — collects any {{ input.* }} values, hosts the readiness gate, and starts the run. -->
    <RunWorkflowModal
      v-model:show="showRunModal"
      :project-id="props.project?.id ?? ''"
      :workflow-id="runTarget?.id ?? null"
      :workflow-name="runTarget?.name"
    />
  </div>
</template>

<style lang="scss">

</style>
